import { z } from 'zod';

import { humanize } from '../../catalogue-ui';

/** How a setting is edited. Nodes pick one with `.meta({ widget })`; the rest follow the type. */
export type Widget =
  | 'segmented'
  | 'chips'
  | 'toggle'
  | 'number'
  | 'decimal'
  | 'text'
  | 'textarea'
  | 'select'
  | 'days'
  | 'multiplier'
  | 'persona'
  | 'accounts'
  | 'personas'
  | 'personaLooks'
  | 'collection'
  | 'audioTrack'
  | 'competitors'
  | 'schedule'
  | 'datetime'
  | 'slots'
  | 'captionStyle'
  | 'times'
  | 'tags'
  | 'image'
  | 'reel'
  | 'scenes'
  | 'prompt'
  | 'date'
  | 'timeZone'
  | 'unsupported';

export type FieldSpec = {
  key: string;
  label: string;
  hint?: string;
  widget: Widget;
  optional: boolean;
  /** Choices for segmented, select and chips. */
  options?: string[];
  min?: number;
  max?: number;
  /** Only shown while other fields hold one of these values, e.g. `{ timing: ['daily'] }`. */
  when?: Record<string, string[]>;
  /** The schema's default, for `when` checks on fields left unset. */
  defaultValue?: unknown;
  /** A prompt's default text: shown, and run, while the field is unset. */
  prefill?: string;
};

const KNOWN = new Set<string>([
  'segmented',
  'chips',
  'toggle',
  'number',
  'text',
  'textarea',
  'select',
  'days',
  'multiplier',
  'persona',
  'accounts',
  'personas',
  'personaLooks',
  'collection',
  'audioTrack',
  'competitors',
  'schedule',
  'datetime',
  'slots',
  'captionStyle',
  'times',
  'tags',
  'image',
  'reel',
  'scenes',
  'prompt',
  'date',
  'timeZone',
]);

type Meta = {
  title?: string;
  description?: string;
  widget?: string;
  when?: Record<string, string[]>;
  prefill?: string;
};

/** The config schema's fields, in order, with how to edit each. */
export function describeConfig(schema: z.ZodType): FieldSpec[] {
  if (!(schema instanceof z.ZodObject)) return [];
  const { shape } = schema as z.ZodObject<z.ZodRawShape>;
  return Object.entries(shape).map(([key, field]) => {
    const meta = (z.globalRegistry.get(field as z.ZodType) ?? {}) as Meta;
    let inner = field as z.ZodType;
    let optional = false;
    let defaultValue: unknown;
    while (['default', 'optional', 'nullable'].includes(inner._zod.def.type)) {
      if (inner._zod.def.type === 'optional') optional = true;
      if (inner._zod.def.type === 'default')
        defaultValue = (inner._zod.def as unknown as { defaultValue: unknown })
          .defaultValue;
      inner = (inner._zod.def as unknown as { innerType: z.ZodType }).innerType;
    }
    const spec: FieldSpec = {
      key,
      label: meta.title ?? humanize(key),
      hint: meta.description,
      optional,
      widget: 'unsupported',
      when: meta.when,
      defaultValue,
      prefill: meta.prefill,
    };

    if (inner instanceof z.ZodEnum) spec.options = inner.options.map(String);
    if (inner instanceof z.ZodArray && inner.element instanceof z.ZodEnum) {
      spec.options = inner.element.options.map(String);
    }
    if (inner instanceof z.ZodNumber) {
      spec.min = inner.minValue ?? undefined;
      spec.max = inner.maxValue ?? undefined;
    }

    if (
      meta.widget === 'segmented' &&
      inner instanceof z.ZodNumber &&
      spec.min !== undefined &&
      spec.max !== undefined
    ) {
      spec.options = Array.from({ length: spec.max - spec.min + 1 }, (_, i) =>
        String(spec.min! + i),
      );
    }
    spec.widget = KNOWN.has(meta.widget ?? '')
      ? (meta.widget as Widget)
      : inferred(inner, spec);
    if (spec.widget === 'number' && inner instanceof z.ZodNumber && !inner.isInt)
      spec.widget = 'decimal';
    return spec;
  });
}

function inferred(inner: z.ZodType, spec: FieldSpec): Widget {
  if (inner instanceof z.ZodEnum)
    return (spec.options?.length ?? 0) <= 4 ? 'segmented' : 'select';
  if (inner instanceof z.ZodBoolean) return 'toggle';
  if (inner instanceof z.ZodNumber) return inner.isInt ? 'number' : 'decimal';
  if (inner instanceof z.ZodArray && spec.options) return 'chips';
  if (inner instanceof z.ZodString || inner instanceof z.ZodUnion) return 'text';
  return 'unsupported';
}

/** Whether a field applies, given the values of the fields its `when` names. */
export function isShown(
  field: FieldSpec,
  config: Record<string, unknown>,
  fields: FieldSpec[],
): boolean {
  return Object.entries(field.when ?? {}).every(([key, values]) => {
    const value = config[key] ?? fields.find((f) => f.key === key)?.defaultValue;
    return values.includes(String(value));
  });
}

/** Validation messages per field (and ones that belong to no field). */
export function configErrors(
  schema: z.ZodType,
  config: unknown,
): { byField: Map<string, string>; general: string[] } {
  const byField = new Map<string, string>();
  const general: string[] = [];
  const parsed = schema.safeParse(config);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const key = issue.path[0];
      if (typeof key === 'string') {
        if (!byField.has(key)) byField.set(key, issue.message);
      } else general.push(issue.message);
    }
  }
  return { byField, general };
}
